import { useMemo } from "react";
import { Check, AlertCircle, MinusCircle } from "lucide-react";

/** Compute a calm SEO health summary for an article draft.
 * Buckets:
 *   - "good": all critical fields strong
 *   - "needs_work": critical present but one or more weak
 *   - "missing": one or more critical fields blank
 */
function evaluate(form) {
    const checks = [];
    const seoTitle = (form.seo_title || form.title || "").trim();
    const meta = (form.meta_description || form.excerpt || "").trim();
    const focus = (form.focus_keyword || "").trim().toLowerCase();
    const slug = (form.slug || "").trim();
    const featured = (form.featured_image_url || "").trim();

    checks.push({
        key: "seo_title",
        label: "SEO title (30–70 chars)",
        status:
            !seoTitle
                ? "missing"
                : seoTitle.length < 30
                  ? "needs_work"
                  : seoTitle.length > 70
                    ? "needs_work"
                    : "good",
        detail: `${seoTitle.length} chars`,
    });
    checks.push({
        key: "meta_description",
        label: "Meta description (120–160 chars)",
        status:
            !meta
                ? "missing"
                : meta.length < 120
                  ? "needs_work"
                  : meta.length > 160
                    ? "needs_work"
                    : "good",
        detail: `${meta.length} chars`,
    });
    checks.push({
        key: "slug",
        label: "URL slug",
        status: !slug ? "missing" : slug.length > 75 ? "needs_work" : "good",
        detail: `${slug.length} chars`,
    });
    checks.push({
        key: "focus_keyword",
        label: "Focus keyword",
        status: !focus
            ? "needs_work"
            : focus && seoTitle.toLowerCase().includes(focus)
              ? "good"
              : "needs_work",
        detail: focus
            ? seoTitle.toLowerCase().includes(focus)
                ? "Used in title"
                : "Not yet in title"
            : "Add one for tracking",
    });
    checks.push({
        key: "featured_image",
        label: "Featured / Open Graph image",
        status: !featured ? "needs_work" : "good",
        detail: featured ? "Set" : "Recommended",
    });

    const overall = checks.some((c) => c.status === "missing")
        ? "missing"
        : checks.some((c) => c.status === "needs_work")
          ? "needs_work"
          : "good";
    return { checks, overall };
}

function StatusIcon({ status }) {
    if (status === "good") {
        return <Check className="w-3.5 h-3.5 text-bl-primary" />;
    }
    if (status === "missing") {
        return <MinusCircle className="w-3.5 h-3.5 text-rose-700" />;
    }
    return <AlertCircle className="w-3.5 h-3.5 text-amber-600" />;
}

const OVERALL_LABEL = {
    good: { text: "Good", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    needs_work: {
        text: "Needs work",
        cls: "bg-amber-50 text-amber-800 border-amber-200",
    },
    missing: {
        text: "Missing critical fields",
        cls: "bg-rose-50 text-rose-800 border-rose-200",
    },
};

export default function SeoPanel({ form, onChange }) {
    const evalu = useMemo(() => evaluate(form), [form]);
    const ovr = OVERALL_LABEL[evalu.overall];

    return (
        <section
            className="bl-card p-5 space-y-4"
            data-testid="cms-seo-panel"
        >
            <div className="flex items-center justify-between">
                <div>
                    <div className="bl-eyebrow">Search optimization</div>
                    <h3 className="font-display font-semibold text-lg text-bl mt-1">
                        SEO
                    </h3>
                </div>
                <span
                    className={
                        "text-xs font-medium px-3 py-1 rounded-full border " +
                        ovr.cls
                    }
                    data-testid={`cms-seo-status-${evalu.overall}`}
                >
                    {ovr.text}
                </span>
            </div>

            <div className="space-y-3">
                <label className="block">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted flex justify-between">
                        <span>SEO title</span>
                        <span className="text-bl-muted font-mono-bl">
                            {(form.seo_title || "").length}/70
                        </span>
                    </div>
                    <input
                        type="text"
                        value={form.seo_title || ""}
                        onChange={(e) => onChange({ seo_title: e.target.value })}
                        placeholder={form.title}
                        maxLength={90}
                        className="mt-1 w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                        data-testid="cms-seo-title-input"
                    />
                </label>

                <label className="block">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted flex justify-between">
                        <span>Meta description</span>
                        <span className="text-bl-muted font-mono-bl">
                            {(form.meta_description || "").length}/160
                        </span>
                    </div>
                    <textarea
                        value={form.meta_description || ""}
                        onChange={(e) =>
                            onChange({ meta_description: e.target.value })
                        }
                        rows={3}
                        maxLength={200}
                        placeholder={form.excerpt}
                        className="mt-1 w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                        data-testid="cms-meta-description-input"
                    />
                </label>

                <label className="block">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Focus keyword
                    </div>
                    <input
                        type="text"
                        value={form.focus_keyword || ""}
                        onChange={(e) =>
                            onChange({ focus_keyword: e.target.value })
                        }
                        placeholder="e.g. foundation cracks"
                        className="mt-1 w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                        data-testid="cms-focus-keyword-input"
                    />
                </label>

                <label className="block">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Featured image URL
                    </div>
                    <input
                        type="url"
                        value={form.featured_image_url || ""}
                        onChange={(e) =>
                            onChange({ featured_image_url: e.target.value })
                        }
                        placeholder="https://…/image.jpg"
                        className="mt-1 w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary font-mono-bl text-xs"
                        data-testid="cms-featured-image-input"
                    />
                </label>

                <label className="block">
                    <div className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                        Featured image alt text
                    </div>
                    <input
                        type="text"
                        value={form.featured_image_alt || ""}
                        onChange={(e) =>
                            onChange({ featured_image_alt: e.target.value })
                        }
                        placeholder="Describe the image for screen readers"
                        className="mt-1 w-full px-3 py-2 bg-bl-bg rounded-lg border border-bl text-bl text-sm outline-none focus:border-bl-primary"
                        data-testid="cms-featured-image-alt-input"
                    />
                </label>
            </div>

            <div
                className="pt-3 border-t border-bl space-y-1.5"
                data-testid="cms-seo-checks"
            >
                {evalu.checks.map((c) => (
                    <div
                        key={c.key}
                        className="flex items-center justify-between text-xs"
                    >
                        <span className="flex items-center gap-2 text-bl">
                            <StatusIcon status={c.status} />
                            {c.label}
                        </span>
                        <span className="text-bl-muted font-mono-bl">
                            {c.detail}
                        </span>
                    </div>
                ))}
            </div>
        </section>
    );
}
